import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Skeleton,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ReportProblemRounded } from "@mui/icons-material";
import { FormProvider, useForm } from "react-hook-form";
import Termination from "./Components/Termination/Termination";
import Lottery from "./Components/Lottery/Lottery";
import Payments from "./Components/Payments/Payments";
import "./Clearings.css";
import { getClearings } from "apis/controllers/clearings";
import { useSelector } from "react-redux";

const Clearings = ({ seasons = [] }) => {
  const [inspectionSummary, setInspectionSummary] = React.useState([]);
  const [lotteryDrawSummary, setLotteryDrawSummary] = React.useState([]);
  const [paymentDecisions, setPaymentDecisions] = React.useState([]);
  const [canAddNewPayment, setCanAddNewPayment] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const { seasonId } = useSelector(state => state.auth);

  const fetchClearings = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      // Fake delay for testing (2 seconds)

      const res = await getClearings({ seasonId: seasonId ?? 2025 });

      if (res?.status === "success") {
        setInspectionSummary(res?.data?.inspectionSummary || []);
        setLotteryDrawSummary(res?.data?.lotteryDrawSummary || []);
        setPaymentDecisions(res?.data?.paymentDecisions || []);
        setCanAddNewPayment(res?.data?.canAddNewPayment || false);
      } else {
        setErrorMessage(res?.message || "Σφάλμα κατά την ανάκτηση δεδομένων.");
      }
    } catch (error) {
      console.error("Error fetching clearings data:", error);
      setErrorMessage("Σφάλμα κατά την ανάκτηση δεδομένων ελέγχου.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClearings();
  }, []);

  const methods = useForm({ defaultValues: { seasonId: "" } });

  const renderSkeletons = () => (
    <Stack spacing={2} sx={{ width: "100%", maxWidth: 900 }}>
      {[1, 2, 3].map((i) => (
        <Card
          key={i}
          sx={{
            borderRadius: 3,
            boxShadow: 1,
            width: "100%",
          }}
        >
          <CardContent>
            <Skeleton variant="text" width="40%" height={32} sx={{ mb: 1 }} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={80}
              sx={{ borderRadius: 2 }}
            />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  return (
    <FormProvider {...methods}>
      <Box
        className="clearings-container"
        sx={{
          p: 3,
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",

        }}
      >
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              backgroundColor: "rgba(255,255,255,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              Φόρτωση δεδομένων...
            </Typography>
          </Box>
        )}

        {errorMessage ? (
          <Alert
            severity="error"
            variant="filled"
            icon={<ReportProblemRounded />}
            sx={{
              borderRadius: 2,
              maxWidth: 600,
              mt: 4,
              bgcolor: "error.main",
              color: "white",
            }}
          >
            {errorMessage}
          </Alert>
        ) : loading ? (
          renderSkeletons()
        ) : (
          <Stack spacing={2} sx={{ width: "100%" }}>
            <Typography variant="h3" gutterBottom>
              Διαδικασία Ελέγχου - Περίοδος {seasonId ?? 2025}
            </Typography>
            <Termination seasonId={seasonId ?? 2025} data={inspectionSummary} fetchClearings={fetchClearings} />
            <Lottery seasonId={seasonId ?? 2025} data={lotteryDrawSummary} fetchClearings={fetchClearings} />
            <Payments seasonId={seasonId ?? 2025} data={paymentDecisions} canAddNewPayment={canAddNewPayment} fetchClearings={fetchClearings} />
          </Stack>
        )}
      </Box>
    </FormProvider>
  );
};

export default Clearings;
